"use client"

import type React from "react"
import { useCallback } from "react"

interface UseNotificationHandlersProps {
  activePhone: string | null
  suppressNextSelectRef: React.MutableRefObject<boolean>
  myUsers: any[]
  usersCache?: { [userId: string]: any }
  setActiveNotification: (notification: any) => void
  setActivePhone: (phone: string | null) => void
  setSelectedConversationId: (id: string | null) => void
  clearUnusedRecentUsers: () => void
  setIsTransferNotification: (value: boolean) => void
  onSelectUser: (phone: string, userId: string | number) => void
  session?: any
}

export const useNotificationHandlers = ({
  activePhone,
  suppressNextSelectRef,
  myUsers,
  usersCache = {},
  setActiveNotification,
  setActivePhone,
  setSelectedConversationId,
  clearUnusedRecentUsers,
  setIsTransferNotification,
  onSelectUser,
  session,
}: UseNotificationHandlersProps) => {
  const handleNotificationClick = useCallback(
    async (
      userId: string | number,
      phone: string,
      type: string,
      urgent: boolean,
      date: Date,
      important: boolean,
      ignored: boolean,
      read: boolean,
      collaborations: any[],
      assigned_by: string,
      transferId?: number,
    ) => {

      const newNotification = {
        userId,
        important,
        urgent,
        read,
        transferId,
        collaborations,
        assigned_by,
        date,
      };
      const isSameNotification = activePhone === phone && 
        window.activeNotificationRef?.transferId === transferId &&
        String(window.activeNotificationRef?.userId) === String(userId);

      if (isSameNotification) {
        setActiveNotification(null);
        setActivePhone(null);
        setSelectedConversationId(null);
        return;
      }

      setActiveNotification(newNotification);
      window.activeNotificationRef = newNotification;

      if (suppressNextSelectRef.current) {
        suppressNextSelectRef.current = false;
        return;
      }

      clearUnusedRecentUsers();

      if (urgent) {
        setIsTransferNotification(true);
      }


      let userPhone = phone;
      
      if (!userPhone) {
        const cachedUser = usersCache[String(userId)];
        if (cachedUser?.phone) {
          userPhone = cachedUser.phone;
        } else {
          const user = myUsers.find((u) => String(u.id) === String(userId));
          if (user?.phone) {
            userPhone = user.phone;
          }
        }
      }

      if (!userPhone && session) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/user/data/${userId}`,
            {
              method: "GET",
              headers: {
                Authorization: `${session.token_type} ${session.access_token}`,
              },
            }
          );
          
          if (response.ok) {
            const userData = await response.json();
            userPhone = userData.phone;
          }
        } catch (error) {
          console.error('Error fetching user phone:', error);
        }
      }

      if (userPhone) {
        onSelectUser(userPhone, userId);
      }
    },
    [
      activePhone,
      suppressNextSelectRef,
      myUsers,
      usersCache,
      setActiveNotification,
      setActivePhone,
      setSelectedConversationId,
      clearUnusedRecentUsers,
      setIsTransferNotification,
      onSelectUser,
      session,
    ],
  )

  const handleUserSelect = useCallback(
    (user: any) => {
      if (activePhone === user.phone) {
        setActiveNotification(null)
        setActivePhone(null)
        setSelectedConversationId(null)
        window.activeNotificationRef = null;
      } else {
        onSelectUser(user.phone, user.id)
      }
    },
    [activePhone, setActiveNotification, setActivePhone, setSelectedConversationId, onSelectUser],
  )

  return {
    handleNotificationClick,
    handleUserSelect,
  }
}
